// Auth interactions for the storefront: sign-in (a full dialog for the
// mobile bar and portal cards, a compact hover popover for the desktop
// navbar pill), logout, and the account menu opening on hover.
//
// The mobile bottom bar and portal sign-in cards keep their real
// href="/login…" and are tagged [data-imek-login]; that click is intercepted
// to open the full dialog instead, so a guest who hits "Login" while reading
// a product stays on that product. The desktop pill instead becomes a
// Bootstrap dropdown-toggle (see navbar_items.html) — its popover behaves
// like the account menu: click OR hover opens it, Popper positions it,
// outside click/Escape closes it. Both paths degrade to the real /login page
// with JS off, or if the relevant markup is ever missing.
//
// Submission posts to /api/method/login, which answers 200 with
// {message, home_page, full_name} or 401 {exc_type: "AuthenticationError"}.
// CSRF is not a concern: a guest session has no token, so frappe skips the
// check (see auth.validate_csrf_token).
(function () {
	"use strict";

	var LOGIN_TRIGGER = "[data-imek-login]";
	var LOGOUT_TRIGGER = "[data-imek-logout]";
	var isDesktop = function () {
		return window.innerWidth >= 992;
	};
	var last_focused = null;
	var redirect_after = null;
	var authentication_pending = false;

	function set_authentication_pending(pending, button) {
		authentication_pending = pending;
		document.querySelectorAll("[data-imek-login-submit]").forEach(function (submit_button) {
			submit_button.disabled = pending;
			submit_button.classList.toggle("is-busy", pending && submit_button === button);
			submit_button.setAttribute("aria-busy", String(pending && submit_button === button));
		});
	}

	function modal() {
		return document.querySelector("[data-imek-login-modal]");
	}

	function popover() {
		return document.querySelector("[data-imek-login-popover]");
	}

	// Whichever of the two containers a given form/button/error lives in —
	// submit() and set_error() work on either without caring which.
	function login_box_of(el) {
		return el.closest("[data-imek-login-modal], [data-imek-login-popover]");
	}

	function focusables(root) {
		return Array.prototype.filter.call(
			root.querySelectorAll("a[href], button:not([disabled]), input:not([disabled])"),
			function (el) {
				return el.offsetParent !== null;
			}
		);
	}

	function set_error(box, message) {
		var error = box.querySelector("[data-imek-login-error]");
		if (!error) {
			return;
		}
		error.textContent = message || "";
		error.hidden = !message;
	}

	// Toggles one password field between masked and plain text. `button` is
	// the eye icon sitting next to that specific field (there are two on the
	// page — modal and popover render independently — so this only ever
	// touches the one the user actually clicked, found via closest()).
	function toggle_password_visibility(button) {
		var wrap = button.closest(".imek-login-pwd-wrap");
		var field = wrap && wrap.querySelector("input");
		if (!field) {
			return;
		}

		var showing = field.type === "text";
		field.type = showing ? "password" : "text";
		button.setAttribute("aria-pressed", String(!showing));
		button.setAttribute("aria-label", showing ? __("Show password") : __("Hide password"));
		wrap.querySelector(".imek-login-pwd-eye").hidden = !showing;
		wrap.querySelector(".imek-login-pwd-eye-off").hidden = showing;

		// keep typing right where it was — toggling `type` moves focus to the
		// end of the value in some browsers otherwise
		var caret = field.selectionStart;
		field.focus();
		if (caret !== null) {
			field.setSelectionRange(caret, caret);
		}
	}

	function open_modal(trigger) {
		var box = modal();
		if (!box) {
			return false;
		}

		// Honour the redirect the link was going to use (portal cards link to
		// /login?redirect-to=/dealer-panel and similar). Falls back to
		// reloading whatever the guest was reading.
		redirect_after = null;
		var href = trigger && trigger.getAttribute("href");
		if (href) {
			try {
				var target = new URL(href, window.location.origin).searchParams.get("redirect-to");
				if (target && target.charAt(0) === "/") {
					redirect_after = target;
				}
			} catch (e) {
				redirect_after = null;
			}
		}

		last_focused = trigger || document.activeElement;
		set_error(box, "");
		box.hidden = false;
		document.body.classList.add("imek-login-open");

		var first = box.querySelector("[name=usr]");
		if (first) {
			first.focus();
		}
		return true;
	}

	// The popover gets NO autofocus, deliberately: unlike the modal (a
	// deliberate, full-attention action) this can open from a plain hover, and
	// stealing keyboard focus just because the cursor passed over the pill
	// would be a genuinely bad surprise. Reset happens on open (not close) so
	// a stale error from a previous attempt cannot flash before the fields
	// clear.
	//
	// Wired ONLY to show.bs.dropdown, never to the hover handler. Bootstrap's
	// show() returns early when the menu is already open, so that event fires
	// once per real open — whereas mouseenter fires every time the cursor
	// crosses back over the element. Resetting there wiped whatever the
	// browser's password manager had just filled in, because choosing a saved
	// password REQUIRES the cursor to leave and come back: the suggestion list
	// is painted outside the page, so reaching it is a mouseleave and clicking
	// it is a mouseenter on the way back.
	// form.reset() restores VALUES but not the `type` a toggle click switched
	// to (it is not a form field default), so without this an unmasked field
	// would still read as text after the fields are cleared — right after
	// showing "your password is now blank" is exactly the wrong moment to
	// leave it unmasked.
	function reset_password_visibility(box) {
		var wrap = box.querySelector(".imek-login-pwd-wrap");
		if (!wrap) {
			return;
		}
		var field = wrap.querySelector("input");
		var toggle = wrap.querySelector("[data-imek-pwd-toggle]");
		field.type = "password";
		toggle.setAttribute("aria-pressed", "false");
		toggle.setAttribute("aria-label", __("Show password"));
		wrap.querySelector(".imek-login-pwd-eye").hidden = false;
		wrap.querySelector(".imek-login-pwd-eye-off").hidden = true;
	}

	function reset_popover() {
		var box = popover();
		if (!box) {
			return;
		}
		var form = box.querySelector("[data-imek-login-form]");
		if (form) {
			form.reset();
		}
		reset_password_visibility(box);
		set_error(box, "");
	}

	function close_modal() {
		var box = modal();
		if (!box || box.hidden) {
			return;
		}
		box.hidden = true;
		document.body.classList.remove("imek-login-open");

		var form = box.querySelector("[data-imek-login-form]");
		if (form) {
			form.reset();
		}
		reset_password_visibility(box);
		set_error(box, "");

		// return focus where it came from, or the dialog is a dead end for
		// anyone navigating by keyboard
		if (last_focused && document.contains(last_focused)) {
			last_focused.focus();
		}
		last_focused = null;
	}

	function submit(box) {
		if (authentication_pending) return;
		var form = box.querySelector("[data-imek-login-form]");
		var button = box.querySelector("[data-imek-login-submit]");
		var usr = (form.querySelector("[name=usr]").value || "").trim();
		var pwd = form.querySelector("[name=pwd]").value || "";

		if (!usr || !pwd) {
			set_error(box, __("Enter your email or mobile and your password."));
			form.querySelector(!usr ? "[name=usr]" : "[name=pwd]").focus();
			return;
		}

		set_error(box, "");
		set_authentication_pending(true, button);

		function fail(message) {
			set_authentication_pending(false);
			set_error(box, message);
			var pwd_field = form.querySelector("[name=pwd]");
			pwd_field.value = "";
			pwd_field.focus();
		}

		// Deliberately NOT frappe.call. A wrong password answers 401, and
		// frappe's built-in 401 handler (request.js) does:
		//     frappe.app.session_expired_dialog ... frappe.app.handle_session_expired()
		// `frappe.app` is the DESK app object and is undefined on a storefront
		// page, so that line throws TypeError before it ever reaches the
		// caller's error callback — and request.js wraps the whole thing in
		// try/catch, so the throw is swallowed to console. Net effect: a wrong
		// password did absolutely nothing, no message, no reset button.
		//
		// Passing our own `statusCode` map (as frappe's own login page tries to)
		// would not help either: frappe.call does not forward statusCode to
		// frappe.request.call, and request.call builds its handler map as a
		// fresh literal that is never merged with the caller's — so those
		// handlers are silently ignored. Owning the request outright is the
		// only way to reliably see our own failures.
		var request = new XMLHttpRequest();
		request.open("POST", "/api/method/login", true);
		request.setRequestHeader("Content-Type", "application/x-www-form-urlencoded");
		request.setRequestHeader("Accept", "application/json");
		// Guests carry no CSRF token and frappe skips the check for them, but
		// send it when present so this keeps working if that ever changes.
		//
		// "None" is filtered explicitly. Frappe stamps the token into the page
		// by interpolating it into an f-string (BaseTemplatePage.add_csrf_token),
		// so a session without one ships the literal text `frappe.csrf_token =
		// "None"` -- a non-empty, THEREFORE TRUTHY, JS string. A bare
		// `if (frappe.csrf_token)` sends that as the header, and any session
		// that does hold a real token then fails validation with 400. Guests
		// happen to survive it (no saved token, so the check is skipped), which
		// is exactly what keeps this quiet until it isn't.
		var csrf = window.frappe && frappe.csrf_token;
		if (csrf && csrf !== "None") {
			request.setRequestHeader("X-Frappe-CSRF-Token", csrf);
		}

		request.onload = function () {
			if (request.status === 200) {
				// full reload, not a DOM patch: prices, cart, the account chip
				// and most page bodies are server-rendered per session, so
				// anything short of this leaves a half-signed-in page.
				// Keep the indicator through navigation handoff; no duration timer.
				window.addEventListener("pagehide", function () {
					set_authentication_pending(false);
				}, { once: true });
				window.location.href = redirect_after || window.location.href;
				return;
			}
			if (request.status === 401) {
				fail(__("Invalid login credentials. Please try again."));
				return;
			}
			if (request.status === 429) {
				fail(__("Too many attempts. Please try again in a few minutes."));
				return;
			}
			fail(__("Could not sign you in. Please try again."));
		};

		request.onerror = function () {
			fail(__("Could not reach the server. Check your connection and try again."));
		};
		request.onabort = request.ontimeout = request.onerror;

		try {
			request.send(
				"usr=" + encodeURIComponent(usr) + "&pwd=" + encodeURIComponent(pwd)
			);
		} catch (error) {
			request.onerror();
		}
	}

	// Frappe's own frappe.logout() (run by www/logout.html on window.onload)
	// hardcodes the post-logout redirect to /login. Bypass that page instead
	// of fighting its timing: call the same whitelisted "logout" method
	// ourselves and send the browser home.
	function do_logout() {
		frappe.call({
			method: "logout",
			callback: function () {
				window.location.href = "/";
			}
		});
	}

	// Opens a Bootstrap dropdown-toggle on hover, in addition to the existing
	// click/keyboard toggle it already provides — click still works untouched,
	// this only adds a second way in for a mouse. Desktop only: every caller
	// here is inside .imek-account-nav-link, hidden entirely under 992px
	// (webshop_mobile.scss), where the bottom bar owns the equivalent entry.
	// Goes through jQuery's dropdown('show'/'hide') rather than toggling .show
	// directly so Popper still positions the menu exactly as a real click
	// would (see frappe-web.bundle.js — dropdown-toggle is Popper-backed).
	//
	// The trigger and the menu are both children of `navSelector`, but the
	// menu is absolutely positioned (Popper) and separated from the trigger by
	// a margin-top gap. Position:absolute takes the menu out of flow, so it
	// does not extend the parent's own rendered box — its
	// getBoundingClientRect() covers only the trigger row. Moving the mouse
	// straight down from the trigger toward the menu crosses that gap band,
	// where elementFromPoint resolves to a sibling (the header's own
	// container), NOT a descendant of the parent — so mouseleave fired
	// mid-transit and closed the menu before the cursor ever reached it
	// (measured on the account menu: an 8px gap, confirmed not-a-descendant at
	// its midpoint; the login popover shares the same structure).
	//
	// Bridging the gap with CSS would be fragile (Popper repositions the menu,
	// so its exact offset is not fixed); a short close delay is the standard
	// fix for this class of hover-menu and works regardless of the gap's size.
	// mouseenter (which re-fires the instant the cursor lands on the menu,
	// itself a descendant of the parent) cancels it, so a normal cursor path
	// never sees the menu close; only actually leaving for good does.
	function bind_hover_dropdown(navSelector) {
		var nav = document.querySelector(navSelector);
		var trigger = nav && nav.querySelector("[data-toggle=dropdown]");
		if (!nav || !trigger) {
			return;
		}

		var close_timer = null;

		function cancel_close() {
			if (close_timer) {
				clearTimeout(close_timer);
				close_timer = null;
			}
		}

		nav.addEventListener("mouseenter", function () {
			if (isDesktop()) {
				cancel_close();
				jQuery(trigger).dropdown("show");
			}
		});

		nav.addEventListener("mouseleave", function () {
			if (!isDesktop()) {
				return;
			}
			cancel_close();
			close_timer = setTimeout(function () {
				close_timer = null;

				// Never pull the panel out from under someone using it. The
				// case that forced this: clicking the password field opens the
				// BROWSER's own password/autofill suggestion list, which is
				// painted outside the page entirely — so moving the mouse onto
				// a suggestion leaves this element's box, fires mouseleave, and
				// the panel vanished mid-interaction with the user's cursor
				// heading for it. Focus stays in the field throughout (the
				// native list takes no DOM focus), so that is the reliable
				// signal that the panel is in use.
				//
				// Scoped to the MENU, not the whole nav: bootstrap's show()
				// focuses the TRIGGER, which lives in the nav too, so testing
				// the nav meant focus was always "inside" and the panel could
				// never close on hover-away at all.
				var menu = nav.querySelector(".dropdown-menu");
				if (menu && menu.contains(document.activeElement)) {
					return;
				}

				jQuery(trigger).dropdown("hide");
			}, 250);
		});
	}

	// Bootstrap's dropdown plugin closes ANY open dropdown on a document click,
	// with two narrow, hand-picked exceptions: a click landing directly on an
	// <input>/<textarea> inside it, and a delegated .stopPropagation() bound to
	// ".dropdown form" — so a click on, say, the submit BUTTON (not an input,
	// tagName mismatch) is only safe because it happens to sit inside that
	// <form>. Anything in this popover that is NOT inside the form — the
	// "Forgot password?" link sits beside it, not inside it — has neither
	// protection and would close the popover on click, right before the
	// browser even gets to act on the click.
	//
	// Rather than lean on which markup happens to satisfy which of Bootstrap's
	// two internal exceptions (and needing to re-verify that alignment every
	// time this markup changes), make the whole popover unconditionally safe:
	// capture the click before it ever reaches Bootstrap's document-level
	// listener and stop it there. `true` (capture phase) is what makes the
	// ordering guaranteed — capture listeners always run top-down, before any
	// bubble-phase listener (which is how Bootstrap's is bound) gets a look at
	// the event, regardless of registration order.
	// The password show/hide toggle is handled HERE, in this same
	// capture-phase listener, rather than in the ordinary bubble-phase click
	// handler below. A click on the toggle button inside an open popover has
	// its propagation stopped right here before it can reach ANY bubble-phase
	// listener — including our own — so handling it in the bubble-phase
	// handler worked for the modal (not guarded by this listener) but silently
	// did nothing for the popover. event.target is already the real clicked
	// element at this point regardless of phase, so resolving it here for
	// both containers in one place is correct and avoids that split.
	document.addEventListener(
		"click",
		function (event) {
			var pwd_toggle = event.target.closest && event.target.closest("[data-imek-pwd-toggle]");
			if (pwd_toggle) {
				event.preventDefault();
				toggle_password_visibility(pwd_toggle);
			}

			var box = popover();
			if (box && box.classList.contains("show") && box.contains(event.target)) {
				event.stopPropagation();
			}
		},
		true
	);

	function init() {
		bind_hover_dropdown(".imek-account-nav");
		// Login opens deliberately by click/keyboard, never while passing the header.

		var login_pop_nav = document.querySelector(".imek-login-nav");
		var login_pop_trigger = login_pop_nav && login_pop_nav.querySelector("[data-toggle=dropdown]");
		if (login_pop_nav) {
			// Bound to the PARENT, not the toggle: bootstrap fires this with
			// $(parentElement).trigger(...), and events bubble up rather than
			// down, so a handler on the toggle itself never receives it.
			// Covers both ways in (hover and click) — bootstrap fires it once
			// per actual open, and not at all when the menu is already showing.
			jQuery(login_pop_nav).on("show.bs.dropdown", reset_popover);
			jQuery(login_pop_nav).on("shown.bs.dropdown", function () {
				var box = popover();
				if (box) box.querySelector("[name=usr]").focus();
			});
		}
		document.addEventListener("input", function (event) {
			var box = login_box_of(event.target);
			if (box) set_error(box, "");
		});

		document.addEventListener("click", function (event) {
			var trigger = event.target.closest && event.target.closest(LOGIN_TRIGGER);
			if (trigger) {
				// open_modal returns false when the dialog is not on the page;
				// let the browser follow the link in that case.
				if (open_modal(trigger)) {
					event.preventDefault();
				}
				return;
			}

			if (event.target.closest && event.target.closest(LOGOUT_TRIGGER)) {
				event.preventDefault();
				do_logout();
				return;
			}

			if (event.target.closest && event.target.closest("[data-imek-login-close]")) {
				event.preventDefault();
				close_modal();
			}
		});

		document.addEventListener("keydown", function (event) {
			if (event.key !== "Escape") {
				// Tab-trapping only makes sense for the modal — a true dialog
				// that owns the whole page's attention. The popover is a
				// dropdown; letting Tab run past it into the rest of the page
				// is the normal, expected way a dropdown closes.
				var box = modal();
				if (event.key === "Tab" && box && !box.hidden) {
					var items = focusables(box);
					if (!items.length) {
						return;
					}
					var first = items[0];
					var last = items[items.length - 1];
					if (event.shiftKey && document.activeElement === first) {
						event.preventDefault();
						last.focus();
					} else if (!event.shiftKey && document.activeElement === last) {
						event.preventDefault();
						first.focus();
					}
				}
				return;
			}

			var open_modal_box = modal();
			if (open_modal_box && !open_modal_box.hidden) {
				close_modal();
				return;
			}

			var open_pop = popover();
			if (open_pop && open_pop.classList.contains("show")) {
				jQuery(login_pop_trigger).dropdown("hide");
				if (login_pop_trigger) {
					login_pop_trigger.focus();
				}
			}
		});

		document.addEventListener("submit", function (event) {
			if (event.target.matches("[data-imek-login-form]")) {
				event.preventDefault();
				submit(login_box_of(event.target));
			}
		});
	}

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", init);
	} else {
		init();
	}
})();
